// Canonical-YAML view (wireframe 10) — three-pane layout: section tree
// (left) + YAML preview of the focused branch (center). Right pin pane
// surfaces schema-validation status + last 5 commits.

import { useMemo, useState } from "react";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

interface TreeLeaf  { label: string; kind: "leaf"; value: unknown; }
interface TreeBranch { label: string; kind: "branch"; child_count: number; children: TreeNode[]; }
type TreeNode = TreeLeaf | TreeBranch;

export default function CanonicalView() {
  const { project, openChat } = useShell();
  const canon = useTool("get_canonical", { project });
  const verify = useTool("verify_state", {});
  const changes = useTool("list_canonical_changes", { limit: 5 });

  const nodes = ((canon.env?.data as { nodes?: TreeNode[] } | undefined)?.nodes ?? []) as TreeNode[];
  // Default-focus the "phases" branch if present, otherwise the first.
  const defaultFocusLabel = useMemo(() => {
    const phaseLike = nodes.find((n) => n.kind === "branch" && /^phases\b/i.test(n.label));
    return (phaseLike ?? nodes[0])?.label ?? null;
  }, [nodes]);
  const [focusLabel, setFocusLabel] = useState<string | null>(null);
  const effectiveFocus = focusLabel ?? defaultFocusLabel;
  const focused = nodes.find((n) => n.label === effectiveFocus) ?? null;

  const verifyText = useMemo(() => {
    const d = verify.env?.data;
    return typeof d === "string" ? d : JSON.stringify(d ?? "", null, 2);
  }, [verify.env]);
  const verifyPassed = verifyText && !/error|fail|✗|×|expected/i.test(verifyText);

  useRightPane(
    useMemo(() => (
      <RightPane
        verifyText={verifyText}
        verifyPassed={!!verifyPassed}
        commits={((changes.env?.data as { rows?: Array<{ short: string; subject: string; date: string }> } | undefined)?.rows ?? [])}
      />
    ), [verifyText, verifyPassed, changes.env]),
  );

  if (canon.loading) return <Skeleton lines={6} />;
  if (canon.error) return <ErrorState error={canon.error} onRetry={canon.refetch} />;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Validation strip */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className={
            "rounded px-2 py-0.5 " +
            (verifyPassed
              ? "bg-emerald-100 text-emerald-700"
              : "bg-amber-100 text-amber-700")
          }>
            {verifyPassed ? "✓ valid · 0 errors" : "⚠ verify_state has warnings"}
          </span>
          <span className="text-slate-500">project.yaml · {project}</span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={() => openChat("Show the recent diff on project.yaml.")}
            className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100"
          >Diff vs HEAD</button>
          <button
            type="button"
            onClick={() => openChat("Rebuild canonical-derived artifacts.")}
            className="rounded border border-slate-200 bg-white px-2 py-1 hover:bg-slate-100"
          >Rebuild artifacts</button>
        </div>
      </div>

      {/* Two-pane body */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700">
            project.yaml structure
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-2 text-xs">
            {nodes.map((n) => (
              <SectionButton
                key={n.label}
                node={n}
                active={n.label === effectiveFocus}
                onPick={(label) => setFocusLabel(label)}
              />
            ))}
          </div>
        </aside>

        <main className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <span className="font-mono text-slate-600">
              projects/{project}/project.yaml · {effectiveFocus ?? "(no section)"}
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {focused == null ? (
              <div className="p-4 text-sm text-slate-500">Select a section on the left.</div>
            ) : (
              <pre className="whitespace-pre p-3 font-mono text-xs leading-5 text-slate-800">
{renderBranch(focused)}
              </pre>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function SectionButton({ node, active, onPick }: {
  node: TreeNode;
  active: boolean;
  onPick: (label: string) => void;
}) {
  const childCount = node.kind === "branch" ? node.child_count : null;
  return (
    <button
      type="button"
      onClick={() => onPick(node.label)}
      className={
        "block w-full truncate rounded px-2 py-1 text-left transition-colors " +
        (active
          ? "bg-slate-900 text-white"
          : "text-slate-700 hover:bg-slate-100")
      }
    >
      <span className="font-medium">{node.label}</span>
      {childCount != null && (
        <span className={"ml-1 text-[10px] " + (active ? "text-slate-300" : "text-slate-400")}>
          [{childCount}]
        </span>
      )}
    </button>
  );
}

function renderBranch(node: TreeNode, depth = 0): string {
  const pad = "  ".repeat(depth);
  if (node.kind === "leaf") {
    return `${pad}${node.label}: ${formatValue(node.value)}`;
  }
  if (node.children.length === 0) {
    return `${pad}${node.label}: []`;
  }
  const lines: string[] = [];
  // For a top-level "phases (N)" / "tasks (N)" branch, render as a YAML list
  // with one block per child; otherwise render as a flat key: value list.
  const isList = /\(\d+\)/.test(node.label) || depth === 0;
  if (isList && depth === 0) {
    // Drop the "(N)" suffix from the rendered key.
    const cleanLabel = node.label.replace(/\s*\(\d+\)\s*$/, "");
    lines.push(`${cleanLabel}:`);
    node.children.forEach((c) => {
      if (c.kind === "leaf") {
        lines.push(`  ${c.label}: ${formatValue(c.value)}`);
      } else {
        const first = (c.children[0]?.kind === "leaf") ? c.children[0] as TreeLeaf : null;
        if (first) {
          lines.push(`  - ${first.label}: ${formatValue(first.value)}`);
          c.children.slice(1).forEach((cc) => {
            if (cc.kind === "leaf") {
              lines.push(`    ${cc.label}: ${formatValue(cc.value)}`);
            } else {
              lines.push(`    ${cc.label}:`);
              lines.push(renderBranch(cc, depth + 3));
            }
          });
        } else {
          lines.push(`  - ${c.label}:`);
          lines.push(renderBranch(c, depth + 2));
        }
      }
    });
    return lines.join("\n");
  }
  lines.push(`${pad}${node.label}:`);
  node.children.forEach((c) => lines.push(renderBranch(c, depth + 1)));
  return lines.join("\n");
}

function formatValue(v: unknown): string {
  if (v == null) return "null";
  if (typeof v === "string") return v.includes("\n") ? `|\n  ${v.replace(/\n/g, "\n  ")}` : v;
  if (Array.isArray(v)) {
    if (!v.length) return "[]";
    return "\n" + v.map((x) => `  - ${typeof x === "string" ? x : JSON.stringify(x)}`).join("\n");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function RightPane({ verifyText, verifyPassed, commits }: {
  verifyText: string;
  verifyPassed: boolean;
  commits: Array<{ short: string; subject: string; date: string }>;
}) {
  // verify_state's stripped output mentions several discrete checks. Parse
  // them best-effort so the wireframe's "5 named checks" reads honestly.
  const checks = useMemo(() => parseChecks(verifyText), [verifyText]);
  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Schema validation</div>
        <ul className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs">
          {checks.length === 0 ? (
            <li className="text-slate-500">
              {verifyPassed ? "verify_state reports no errors." : "Could not parse verify_state output."}
            </li>
          ) : checks.map((c) => (
            <li key={c.name} className="flex items-center justify-between">
              <span className="text-slate-700">{c.name}</span>
              <span className={c.ok ? "text-emerald-600" : "text-rose-600"}>
                {c.ok ? "✓ pass" : "✗ fail"}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Last 5 commits</div>
        <ul className="mt-2 space-y-1 rounded border border-slate-200 p-2 text-xs">
          {commits.length === 0 ? (
            <li className="text-slate-500">No git history available.</li>
          ) : commits.map((c) => (
            <li key={c.short}>
              <div className="truncate text-slate-700">{c.subject}</div>
              <div className="text-[10px] text-slate-500">
                <span className="font-mono">{c.short}</span> · {c.date?.slice(0, 10) ?? ""}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function parseChecks(text: string): Array<{ name: string; ok: boolean }> {
  if (!text) return [];
  const checks: Array<{ name: string; ok: boolean }> = [];
  // verify_state output uses lines like "[ infra ] ✓ Colima running = ..." or
  // "[ canonical ] ✗ schema_check: …" — pluck the section name + tick/cross.
  const lines = text.split(/\n+/);
  for (const line of lines) {
    const m = line.match(/^\s*\[\s*([^\]]+?)\s*\]\s*([✓×✗])\s*(.+?)(?:\s*=|$)/);
    if (m) {
      const section = m[1].trim();
      const ok = m[2] === "✓";
      const label = m[3].split(":")[0].trim();
      checks.push({ name: `${section} · ${label}`, ok });
    }
  }
  return checks.slice(0, 8);
}
